/**
 * Error Classification System for K-Skin AI
 * Provides user-friendly error messages and retry logic
 */

export enum AnalysisErrorType {
  NETWORK_ERROR = 'network_error',
  UPLOAD_FAILED = 'upload_failed',
  ANALYSIS_TIMEOUT = 'analysis_timeout',
  FACE_NOT_DETECTED = 'face_not_detected',
  IMAGE_QUALITY = 'image_quality',
  SERVER_ERROR = 'server_error',
  OFFLINE = 'offline',
  UNKNOWN = 'unknown',
}

export interface AnalysisError {
  type: AnalysisErrorType
  title: string
  message: string
  technicalDetails?: string
  canRetry: boolean
  suggestRetake: boolean // Suggests taking a new photo vs just retrying
}

// Error message configurations
const ERROR_CONFIGS: Record<AnalysisErrorType, Omit<AnalysisError, 'technicalDetails'>> = {
  [AnalysisErrorType.NETWORK_ERROR]: {
    type: AnalysisErrorType.NETWORK_ERROR,
    title: 'Connection Issue',
    message: 'We couldn\'t connect to our servers. Please check your internet connection and try again.',
    canRetry: true,
    suggestRetake: false,
  },
  [AnalysisErrorType.UPLOAD_FAILED]: {
    type: AnalysisErrorType.UPLOAD_FAILED,
    title: 'Upload Failed',
    message: 'We couldn\'t upload your photo. Please try again.',
    canRetry: true,
    suggestRetake: false,
  },
  [AnalysisErrorType.ANALYSIS_TIMEOUT]: {
    type: AnalysisErrorType.ANALYSIS_TIMEOUT,
    title: 'Analysis Taking Too Long',
    message: 'Our servers are busy right now. Please try again in a moment.',
    canRetry: true,
    suggestRetake: false,
  },
  [AnalysisErrorType.FACE_NOT_DETECTED]: {
    type: AnalysisErrorType.FACE_NOT_DETECTED,
    title: 'Face Not Detected',
    message: 'We couldn\'t detect a face clearly in your photo. Please take a new photo with your face centered and well-lit.',
    canRetry: false,
    suggestRetake: true,
  },
  [AnalysisErrorType.IMAGE_QUALITY]: {
    type: AnalysisErrorType.IMAGE_QUALITY,
    title: 'Photo Quality Issue',
    message: 'The photo quality isn\'t ideal for analysis. Please take a new photo with better lighting.',
    canRetry: false,
    suggestRetake: true,
  },
  [AnalysisErrorType.SERVER_ERROR]: {
    type: AnalysisErrorType.SERVER_ERROR,
    title: 'Something Went Wrong',
    message: 'We\'re experiencing technical difficulties. Please try again in a moment.',
    canRetry: true,
    suggestRetake: false,
  },
  [AnalysisErrorType.OFFLINE]: {
    type: AnalysisErrorType.OFFLINE,
    title: 'You\'re Offline',
    message: 'Please connect to the internet to analyze your photo.',
    canRetry: true,
    suggestRetake: false,
  },
  [AnalysisErrorType.UNKNOWN]: {
    type: AnalysisErrorType.UNKNOWN,
    title: 'Analysis Failed',
    message: 'Something unexpected happened. Please try again.',
    canRetry: true,
    suggestRetake: false,
  },
}

/**
 * Custom error class for analysis errors
 */
export class SkinAnalysisError extends Error {
  public readonly errorInfo: AnalysisError

  constructor(type: AnalysisErrorType, technicalDetails?: string) {
    const config = ERROR_CONFIGS[type]
    super(config.message)
    this.name = 'SkinAnalysisError'
    this.errorInfo = {
      ...config,
      technicalDetails,
    }
  }
}

/**
 * Classify an error into a user-friendly AnalysisError
 */
export function classifyError(error: unknown): AnalysisError {
  // Already classified
  if (error instanceof SkinAnalysisError) {
    return error.errorInfo
  }

  const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()
  const errorName = error instanceof Error ? error.name : ''

  // Check for offline
  if (!navigator.onLine) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.OFFLINE],
      technicalDetails: 'navigator.onLine is false',
    }
  }

  // Network errors
  if (
    errorName === 'TypeError' && errorMessage.includes('fetch') ||
    errorMessage.includes('network') ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('net::err') ||
    errorMessage.includes('cors')
  ) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.NETWORK_ERROR],
      technicalDetails: error instanceof Error ? error.message : String(error),
    }
  }

  // Upload failures
  if (
    errorMessage.includes('upload') ||
    errorMessage.includes('failed to get upload url') ||
    errorMessage.includes('presigned')
  ) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.UPLOAD_FAILED],
      technicalDetails: error instanceof Error ? error.message : String(error),
    }
  }

  // Timeout
  if (
    errorMessage.includes('timeout') ||
    errorMessage.includes('timed out') ||
    errorMessage.includes('taking too long')
  ) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.ANALYSIS_TIMEOUT],
      technicalDetails: error instanceof Error ? error.message : String(error),
    }
  }

  // Face detection issues - PerfectCorp specific errors
  if (
    errorMessage.includes('face') ||
    errorMessage.includes('no face') ||
    errorMessage.includes('face not found') ||
    errorMessage.includes('face_not_detected') ||
    errorMessage.includes('multiple faces')
  ) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.FACE_NOT_DETECTED],
      technicalDetails: error instanceof Error ? error.message : String(error),
    }
  }

  // Image quality issues
  if (
    errorMessage.includes('quality') ||
    errorMessage.includes('blur') ||
    errorMessage.includes('resolution') ||
    errorMessage.includes('too dark') ||
    errorMessage.includes('too bright')
  ) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.IMAGE_QUALITY],
      technicalDetails: error instanceof Error ? error.message : String(error),
    }
  }

  // Server errors (HTTP 5xx)
  if (
    errorMessage.includes('500') ||
    errorMessage.includes('502') ||
    errorMessage.includes('503') ||
    errorMessage.includes('504') ||
    errorMessage.includes('server error') ||
    errorMessage.includes('internal error')
  ) {
    return {
      ...ERROR_CONFIGS[AnalysisErrorType.SERVER_ERROR],
      technicalDetails: error instanceof Error ? error.message : String(error),
    }
  }

  // Default: unknown error
  return {
    ...ERROR_CONFIGS[AnalysisErrorType.UNKNOWN],
    technicalDetails: error instanceof Error ? error.message : String(error),
  }
}

/**
 * Check if the device is currently online
 */
export function isOnline(): boolean {
  return navigator.onLine
}

/**
 * Create offline error
 */
export function createOfflineError(): SkinAnalysisError {
  return new SkinAnalysisError(AnalysisErrorType.OFFLINE)
}
